import { State } from '@meraki-internal/state';
import { WithLinks } from '../caremap/WithLinks';
import { ICaremapNote, ICaremapNoteResponse, NotesClient } from '../notes/model/NotesClient';
import { AlertPresenter } from '../AlertBinder';
import { BufferedEventBus } from '../utils/BufferedEventBus';
import { IResourceChangedSocketMessage } from '../web-sockets/SocketState';
import { nanoid } from 'nanoid';
import { Logger } from '../support/Logger';
import { CareMapNoteTracker } from '../notes/model/CareMapNoteTracker';
import { INoteInteractionSurface } from '../notes/reactions/INoteInteractionSurface';
import { isExpectedFetchError } from '../support/isExpectedFetchError';

export class CommentsModelFactory extends State<Record<string, never>> {
    static inject = () => [
        NotesClient,
        AlertPresenter,
        BufferedEventBus,
        Logger,
        CareMapNoteTracker
    ];
    constructor(
        public notesClient: NotesClient,
        public alert: AlertPresenter,
        public events: BufferedEventBus,
        public logger: Logger,
        public tracker: CareMapNoteTracker
    ) {
        super({});

        // events allows a limited number of subscribers
        // so subscribe once here and pass the message on
        this.events.on('web-sockets.message', msg => {
            for (const model of Object.values(this.noteIdToModel)) {
                model.onWebsocketMessage(msg as IResourceChangedSocketMessage);
            }
        });
    }

    private noteIdToModel: { [noteId: string]: CommentsModel } = {};

    forNote = (note: ICaremapNote): CommentsModel => {
        if (!this.noteIdToModel[note.noteId!]) {
            const model = new CommentsModel(note, this);

            model.load().catch(err => {
                this.alert.showAndLogError(err);
            });

            // if the model changes, we change, that way components can just subscrive to the factory
            // (likely called in a render, so avoid changing state synchronously)
            setTimeout(() => {
                model.subscribe(() => {
                    this.setState({});
                });
            });

            this.noteIdToModel[note.noteId!] = model;
        }
        const cached = this.noteIdToModel[note.noteId!];
        // the note can be hidden or shared after its model was cached
        cached.setParentNote(note);
        return cached;
    };

    forget = (noteId: string) => {
        delete this.noteIdToModel[noteId];
    };
}

export class CommentsModel extends State<Record<string, never>> {
    constructor(
        private note: ICaremapNote,
        private factory: CommentsModelFactory
    ) {
        super({});
    };

    private comments: ICaremapNoteResponse[] = [];

    private editingComment?: ICaremapNoteResponse;

    setParentNote = (note: ICaremapNote) => {
        this.note = note;
    };

    onWebsocketMessage = async (message: IResourceChangedSocketMessage) => {
        if (message.resourceToRefresh === this.note.links?.comments?.href) {
            try {
                await this.load();
                this.setState({ });
            } catch (err: any) {
                // the note was hidden (403) or deleted (404): drop the model, which would miss comments
                // made meanwhile, so a reopened note loads them afresh
                if (err?.status === 403 || err?.status === 404) {
                    this.factory.forget(this.note.noteId!);
                    return;
                }
                if (!isExpectedFetchError(err)) {
                    this.factory.logger.error(`CommentsModel.onWebsocketMessage failed updating comments for noteId ${this.note.noteId}. Inner error: ${err.toString()}`);
                }
            };
        }
    };

    load = async () => {
        this.comments = await this.factory.notesClient.getCommentsForNote(this.note);
        this.setState({});
    };

    getComments = (): ICaremapNoteResponse[] => {
        return this.comments;
    };

    canAdd = () => {
        return (this.note.links.comments?.actions || []).includes('post-comment');
    };

    addNewComment = async ({ html, surface }: { html: string; surface: INoteInteractionSurface; }) => {
        const response = await this.factory.notesClient.addCommentToNote({
            parentNote: this.note,
            comment: {
                noteId: nanoid(),
                title: '',
                html,
                attachments: [],
                otherAttributes: {
                    noteTarget: { section: 'patient' },
                    parentNoteId: this.note.noteId
                }
            }
        });

        this.factory.tracker.onCommentChanged({ comment: response, parentNote: this.note, action: 'add', surface });

        // when running locally with bypass, comment will already have been added via websocket
        // so only add it here if not already in the list
        if (!this.comments.some(c => c.noteId === response.noteId)) {
            this.comments.push(response);
            this.setState({});
        }

        return response;
    };

    canDelete = (comment: WithLinks<ICaremapNote>) => {
        return (comment.links.self?.actions || []).includes('delete-note');
    };

    deleteComment = async ({ comment, surface }: { comment: WithLinks<ICaremapNote>; surface: INoteInteractionSurface; }) => {
        const deletedNote = this.comments.find(c=>c.noteId === comment.noteId);
        await this.factory.notesClient.deleteNote(comment);

        if (deletedNote) {
            this.factory.tracker.onCommentChanged({ comment: deletedNote, parentNote: this.note, action: 'delete', surface });
        }

        this.comments = this.comments.filter(c => c.noteId !== comment.noteId);
        this.setState({});
    };

    canEdit = (comment: WithLinks<ICaremapNote>) => {
        return (comment.links.self?.actions || []).includes('put-note');
    };

    startEditing = (comment: ICaremapNoteResponse) => {
        this.editingComment = comment;
        this.setState({});
    };

    doneEditing = () => {
        this.editingComment = undefined;
        this.setState({});
    };

    getEditingComment = () => {
        return this.editingComment;
    };

    saveEditing = async ({ comment, surface }: { comment: ICaremapNoteResponse; surface: INoteInteractionSurface; }) => {
        const response = await this.factory.notesClient.updateNote(comment);

        this.factory.tracker.onCommentChanged({ comment: response, parentNote: this.note, action: 'edit', surface });
        this.comments = [
            ...this.comments.filter(c => c.noteId !== comment.noteId),
            response
        ];

        this.doneEditing();
    };
}
